import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { RigidBody, CuboidCollider } from "@react-three/rapier";
import { Text } from "@react-three/drei";
import * as THREE from "three";
import { getRockTexture } from "./textures";

interface CaveEntranceProps {
  position: [number, number, number];
  facingAngle: number;
  label: string;
  accent: string;
  onEnter: () => void;
}

const WIND_COLORS = ["#ff6b6b", "#ffd93d", "#6bcBef", "#8a5fff", "#4fd18a", "#ff9f4f"];

/** Small colorful particles swirling at the cave mouth — purely decorative. */
const WindParticles: React.FC = () => {
  const pointsRef = useRef<THREE.Points>(null);

  const { positions, colors, seeds } = useMemo(() => {
    const count = 40;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const seeds = new Float32Array(count * 2);
    const palette = WIND_COLORS.map((c) => new THREE.Color(c));
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2;
      const radius = 0.6 + Math.random() * 1.4;
      positions[i * 3] = Math.cos(angle) * radius;
      positions[i * 3 + 1] = 0.3 + Math.random() * 2.6;
      positions[i * 3 + 2] = Math.sin(angle) * radius * 0.4 + 0.1;
      const c = palette[i % palette.length];
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      seeds[i * 2] = Math.random() * Math.PI * 2;
      seeds[i * 2 + 1] = 0.5 + Math.random() * 0.8;
    }
    return { positions, colors, seeds };
  }, []);

  useFrame(({ clock }) => {
    if (!pointsRef.current) return;
    const t = clock.getElapsedTime();
    const posAttr = pointsRef.current.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < posAttr.count; i++) {
      const baseAngle = seeds[i * 2];
      const speed = seeds[i * 2 + 1];
      const radius = 0.6 + ((i % 5) * 0.28);
      const angle = baseAngle + t * speed;
      posAttr.setX(i, Math.cos(angle) * radius);
      posAttr.setZ(i, Math.sin(angle) * radius * 0.4 + 0.1);
      posAttr.setY(i, 0.3 + ((Math.sin(t * speed + baseAngle) + 1) / 2) * 2.6);
    }
    posAttr.needsUpdate = true;
  });

  return (
    <points ref={pointsRef} position={[0, 0, 0.4]}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.12} vertexColors transparent opacity={0.85} sizeAttenuation depthWrite={false} />
    </points>
  );
};

/** A rocky archway with a dark tunnel mouth — walking/driving in navigates to that part of the site. */
const CaveEntrance: React.FC<CaveEntranceProps> = ({
  position,
  facingAngle,
  label,
  accent,
  onEnter,
}) => {
  const rock = useMemo(() => getRockTexture(), []);

  return (
    <group position={position} rotation={[0, facingAngle, 0]}>
      {/* Sensor collider — walking/driving into the mouth triggers entry */}
      <RigidBody type="fixed" colliders={false}>
        <CuboidCollider args={[1.6, 1.6, 1.2]} position={[0, 1.6, 0]} sensor onIntersectionEnter={onEnter} />
      </RigidBody>

      {/* Rock mass either side of the mouth */}
      <mesh position={[-2.1, 1.6, -0.5]} rotation={[0.2, 0.4, 0.1]} castShadow>
        <dodecahedronGeometry args={[1.7, 0]} />
        <meshStandardMaterial map={rock} roughness={1} flatShading />
      </mesh>
      <mesh position={[2.1, 1.7, -0.5]} rotation={[0.1, -0.3, 0.15]} castShadow>
        <dodecahedronGeometry args={[1.8, 0]} />
        <meshStandardMaterial map={rock} roughness={1} flatShading />
      </mesh>
      <mesh position={[0, 3.4, -0.8]} rotation={[0.3, 0.1, 0]} castShadow>
        <dodecahedronGeometry args={[1.6, 0]} />
        <meshStandardMaterial map={rock} roughness={1} flatShading />
      </mesh>

      {/* Dark tunnel mouth */}
      <mesh position={[0, 1.4, 0]}>
        <planeGeometry args={[2.2, 2.6]} />
        <meshBasicMaterial color="#080604" />
      </mesh>

      <WindParticles />

      {/* Label plate, positioned over the doorway and well in front of the rocks
          (which reach to z≈1.3) so it stays legible and unoccluded up close */}
      <mesh position={[0, 2.98, 1.5]}>
        <planeGeometry args={[3, 0.6]} />
        <meshBasicMaterial color="#141414" transparent opacity={0.75} />
      </mesh>
      <Text
        position={[0, 2.98, 1.57]}
        fontSize={0.32}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.025}
        outlineColor="#000000"
      >
        {label}
      </Text>
      <mesh position={[0, 2.7, 1.56]}>
        <planeGeometry args={[3, 0.05]} />
        <meshBasicMaterial color={accent} />
      </mesh>
    </group>
  );
};

export default CaveEntrance;
